import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import secureLocalStorage from "react-secure-storage";

export default function BasicSetting() {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    mobile: "",
    password: "",
    confirmPassword: "",
  });

  const [message, setMessage] = useState({ text: "", type: "" });
  const [loading, setLoading] = useState(false);

  // ✅ Load data securely from localStorage
  useEffect(() => {
    const email = secureLocalStorage.getItem("user");
    const name = secureLocalStorage.getItem("name");
    const mobile = secureLocalStorage.getItem("mobile_number");
    setFormData((prev) => ({
      ...prev,
      email: email || "",
      name: name || "",
      mobile: mobile || "",
    }));
  }, []);

  // ✅ Handle Input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setMessage({ text: "Passwords do not match!", type: "error" });
      return;
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
		const payload = {
			userId: import.meta.env.VITE_API_USER,
			password: import.meta.env.VITE_API_PASS,
			user: secureLocalStorage.getItem("user"),
			token:secureLocalStorage.getItem("token"),
			email: formData.email,
			name: formData.name,
			mobile: formData.mobile,
			user_password: formData.password,
		};
		const api = axios.create({
			baseURL: import.meta.env.VITE_API_BASE_URL,
			headers: { "Content-Type": "application/json" },
		});
		const response = await api.post("/basic-setting", payload);
     

      if (response.data?.responseCode === "200") {
        setMessage({ text: "Profile updated successfully!", type: "success" });
      } else {
        setMessage({ text: "Update failed. Please try again.", type: "error" });
      }
    } catch (error) {
      setMessage({ text: "Something went wrong!", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br bg-gradient-to-b from-[#F3F4FF] via-[#E2E2FD] to-[#CECCFB] flex justify-center items-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-lg"
      >
        <Card className="shadow-2xl bg-white/80 backdrop-blur-lg rounded-2xl border border-white/30">
          <CardContent className="p-8">
            <motion.h2
              className="text-3xl font-bold text-center text-[#1A2B63] mb-8"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Basic Settings
            </motion.h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-gray-600 text-sm font-semibold mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  readOnly
                  className="w-full px-4 py-2 rounded-lg border bg-gray-100 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-gray-600 text-sm font-semibold mb-1">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  readOnly
                  className="w-full px-4 py-2 rounded-lg border bg-gray-100 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-gray-600 text-sm font-semibold mb-1">
                  Mobile Number
                </label>
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-600 text-sm font-semibold mb-1">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-600 text-sm font-semibold mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                />
              </div>

              <motion.div whileHover={{ scale: 1.05 }}>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2 bg-[#1A2B63] hover:bg-indigo-800 text-white font-semibold rounded-lg transition"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </motion.div>
            </form>

            {message.text && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-5 text-center font-medium ${
                  message.type === "success"
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {message.text}
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
